import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Badge } from '@capawesome/capacitor-badge';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';

import { markNotificationAsRead, registerDevice } from '../apis/backend';
import { handleNotificationRoute } from '../utils/routing';
import { getVapidKey } from '../config/env';
import { getRevenueCatAppUserId } from '../utils/revenueCatUtils';
import i18n from '../i18n';

// Constants for message types to avoid magic strings
const MESSAGE_TYPES = {
  NOTIFICATION_CLICK: 'NOTIFICATION_CLICK',
} as const;

interface NotificationConfig {
  notificationTypes: string[];
  watchlistSymbols: string[]; // Now populated from portfolios instead of watchlist
}

interface NotificationData {
  notification_type?: string;
  symbols?: string;
  route?: string; // Route to navigate to when notification is tapped
  symbol?: string;
  type?: string;
  [key: string]: any;
}
let isAppInForeground = false; // Default to false
let appStateListenerSetup = false;

/**
 * Clear the app badge on both native and web platforms
 */
const clearBadge = async (context: string) => {
  try {
    if (Capacitor.isNativePlatform()) {
      await Badge.clear();
      console.log(`[PUSH-SERVICE] Badge cleared ${context} (native)`);
    } else if (Capacitor.getPlatform() === 'web') {
      if (navigator && 'clearAppBadge' in navigator) {
        await (navigator as any).clearAppBadge();
        console.log(`[PUSH-SERVICE] Badge cleared ${context} (web)`);
      }
    }
  } catch (error) {
    console.error(`[PUSH-SERVICE] Error clearing badge ${context}:`, error);
  }
};

/**
 * Setup app state listener for badge clearing (only when service is initialized)
 */
const setupAppStateListener = async () => {
  if (appStateListenerSetup) {
    return;
  }

  console.log('[PUSH-SERVICE] Setting up app state listener for badge clearing');

  App.addListener('appStateChange', async (state) => {
    isAppInForeground = state.isActive;
    console.log('[PUSH-SERVICE] App state changed:', isAppInForeground);
    if (isAppInForeground) {
      await clearBadge('on resume');
    }
  });

  const state = await App.getState();
  isAppInForeground = state.isActive;
  appStateListenerSetup = true;

  // Clear badge on initial setup if app is already in foreground (cold start)
  if (isAppInForeground) {
    await clearBadge('on cold start');
  }
};

export class PushNotificationService {
  private static currentToken: string | null = null;
  private static isInitialized = false;
  private static listenersSetup = false;

  // Daily sync constants
  private static readonly DAILY_SYNC_KEY = 'lastDeviceSyncDate';
  private static readonly SYNC_COOLDOWN_HOURS = 24;

  // Session-level protection to prevent multiple sync attempts in same session
  private static dailySyncAttemptedThisSession = false;

  /**
   * Get the current FCM token
   */
  static getCurrentToken(): string | null {
    return this.currentToken;
  }

  /**
   * Perform daily device synchronization with backend
   * This ensures the backend has the latest device configuration even if other sync calls fail
   * Only executes once per day maximum, and only once per session
   */
  static async performDailyDeviceSync(): Promise<boolean> {
    try {
      console.log('[DAILY-SYNC] 🔄 Checking if daily device sync is needed...');

      // Session-level protection: prevent multiple attempts in same session
      if (this.dailySyncAttemptedThisSession) {
        console.log('[DAILY-SYNC] ⏭️ Daily sync already attempted this session, skipping');
        return true;
      }

      // Mark that we've attempted sync this session (regardless of outcome)
      this.dailySyncAttemptedThisSession = true;

      // Check if we should perform sync (once per day maximum)
      if (!this.shouldPerformDailySync()) {
        console.log('[DAILY-SYNC] ⏭️ Daily sync already performed within last 24 hours, skipping');
        return true;
      }

      // Get FCM token
      const token = await this.getToken();
      if (!token) {
        console.warn('[DAILY-SYNC] ❌ No FCM token available, skipping daily sync');
        return false;
      }

      // Get notification preferences
      const savedPreferences = localStorage.getItem('notificationPreferences');
      if (!savedPreferences) {
        console.log('[DAILY-SYNC] ⏭️ No notification preferences found, skipping daily sync');
        return true;
      }

      const preferences = JSON.parse(savedPreferences);
      const enabledTypes = preferences
        .filter((pref: { enabled: boolean }) => pref.enabled)
        .map((pref: { id: string }) => pref.id);

      // Get current portfolio symbols
      const { getPortfolios } = await import('../storage');
      const portfolios = await getPortfolios();
      const allSymbols = Array.from(new Set(portfolios.flatMap((p) => p.symbols || [])));

      console.log('[DAILY-SYNC] 🚀 Performing daily device sync...', {
        token: token.slice(0, 10) + '...',
        enabledNotificationTypes: enabledTypes.length,
        totalSymbols: allSymbols.length,
        platform: Capacitor.getPlatform(),
      });

      // Send sync request to backend
      const rcUserId = await getRevenueCatAppUserId();
      const currentLang = i18n.language?.startsWith('es') ? 'es' : 'en';
      await registerDevice(token, Capacitor.getPlatform(), {
        notificationTypes: enabledTypes,
        watchlistSymbols: allSymbols,
        revenuecatUserId: rcUserId,
        language: currentLang,
      });

      // Update last sync timestamp
      this.updateLastSyncTimestamp();

      console.log('[DAILY-SYNC] ✅ Daily device sync completed successfully');
      return true;
    } catch (error) {
      console.error('[DAILY-SYNC] ❌ Daily device sync failed:', error);
      return false;
    }
  }

  /**
   * Check if daily sync should be performed based on last sync timestamp
   */
  private static shouldPerformDailySync(): boolean {
    try {
      const lastSyncStr = localStorage.getItem(this.DAILY_SYNC_KEY);
      if (!lastSyncStr) {
        console.log('[DAILY-SYNC] 🆕 No previous sync found, sync needed');
        return true;
      }

      const lastSync = new Date(lastSyncStr);
      const now = new Date();
      const hoursSinceLastSync = (now.getTime() - lastSync.getTime()) / (1000 * 60 * 60);

      console.log('[DAILY-SYNC] 🕐 Hours since last sync:', hoursSinceLastSync.toFixed(1));

      return hoursSinceLastSync >= this.SYNC_COOLDOWN_HOURS;
    } catch (error) {
      console.warn('[DAILY-SYNC] ⚠️ Error checking last sync time, assuming sync needed:', error);
      return true;
    }
  }

  /**
   * Update the last sync timestamp in localStorage
   */
  private static updateLastSyncTimestamp(): void {
    try {
      const now = new Date().toISOString();
      localStorage.setItem(this.DAILY_SYNC_KEY, now);
      console.log('[DAILY-SYNC] 📅 Updated last sync timestamp:', now);
    } catch (error) {
      console.warn('[DAILY-SYNC] ⚠️ Failed to update last sync timestamp:', error);
    }
  }

  /**
   * Get the FCM token, handling both native and web platforms
   * This method will attempt to get the token even if the service is not initialized
   */
  static async getToken(): Promise<string | null> {
    // If we already have a token, return it
    if (this.currentToken) {
      console.log('[PUSH-SERVICE] ✅ Returning cached token:', this.currentToken.slice(0, 10) + '...');
      return this.currentToken;
    }

    try {
      const platform = Capacitor.getPlatform();
      console.log('[PUSH-SERVICE] 🔍 Getting FCM token for platform:', platform);

      if (Capacitor.isNativePlatform()) {
        // Check if FirebaseMessaging plugin is available before using it
        if (!Capacitor.isPluginAvailable('FirebaseMessaging')) {
          console.warn('[PUSH-SERVICE] ❌ FirebaseMessaging plugin not available on this device');
          return null;
        }
        console.log('[PUSH-SERVICE] 📱 Using native Firebase Messaging');
        // For native platforms, use Capacitor Firebase Messaging
        const { token } = await FirebaseMessaging.getToken();
        this.currentToken = token || null;
        console.log(
          '[PUSH-SERVICE] 📱 Native token result:',
          this.currentToken ? this.currentToken.slice(0, 10) + '...' : 'null',
        );
        return this.currentToken;
      } else {
        console.log('[PUSH-SERVICE] 💻 Using web Firebase Messaging');

        // For web platform, use Firebase Web SDK
        console.log('[PUSH-SERVICE] 🔍 Validating web environment...');
        if (!this.validateWebEnvironment()) {
          console.warn('[PUSH-SERVICE] ❌ Web environment validation failed');
          return null;
        }
        console.log('[PUSH-SERVICE] ✅ Web environment validated');

        // Check permissions first - do NOT request automatically
        console.log('[PUSH-SERVICE] 🔍 Checking notification permission:', Notification.permission);
        if (Notification.permission !== 'granted') {
          console.warn('[PUSH-SERVICE] ❌ Notification permission not granted');
          console.warn('[PUSH-SERVICE] 🔄 Use PushNotificationService.initialize() to request permissions');
          return null;
        }
        console.log('[PUSH-SERVICE] ✅ Notification permission granted');

        console.log('[PUSH-SERVICE] 🔍 Importing Firebase config...');
        const { getMessagingInstance } = await import('../config/firebase.config');
        const { getToken } = await import('firebase/messaging');
        const messaging = await getMessagingInstance();
        if (!messaging) {
          console.warn('[PUSH-SERVICE] ❌ Firebase messaging not supported in this browser');
          return null;
        }
        console.log('[PUSH-SERVICE] ✅ Firebase config imported');

        // Try to get existing service worker registration
        console.log('[PUSH-SERVICE] 🔍 Setting up service worker...');
        let registration: ServiceWorkerRegistration | undefined;
        try {
          registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
          if (!registration) {
            console.log('[PUSH-SERVICE] 🔄 Registering service worker...');
            registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
            console.log('[PUSH-SERVICE] ✅ Service worker registered');
          } else {
            console.log('[PUSH-SERVICE] ✅ Service worker already registered');
          }
        } catch (error) {
          console.warn('[PUSH-SERVICE] ⚠️ Service worker registration failed:', error);
          console.log('[PUSH-SERVICE] 🔄 Attempting to get token without service worker...');
        }

        console.log('[PUSH-SERVICE] 🔍 Getting FCM token with VAPID key...');
        console.log('[PUSH-SERVICE] 🔍 VAPID key available:', !!getVapidKey());

        const token = await getToken(messaging, {
          vapidKey: getVapidKey(),
          serviceWorkerRegistration: registration,
        });

        this.currentToken = token || null;
        console.log(
          '[PUSH-SERVICE] 💻 Web token result:',
          this.currentToken ? this.currentToken.slice(0, 10) + '...' : 'null',
        );
        return this.currentToken;
      }
    } catch (error) {
      console.error('[PUSH-SERVICE] ❌ Error getting FCM token:', error);
      return null;
    }
  }

  /**
   * Validate environment configuration for web platform
   */
  private static validateWebEnvironment(): boolean {
    console.log('[PUSH-SERVICE] 🔍 Validating web environment...');

    if (!getVapidKey()) {
      console.error('[PUSH-SERVICE] ❌ VITE_APP_VAPID_KEY not configured');
      console.error('[PUSH-SERVICE] 💡 To fix this:');
      console.error('[PUSH-SERVICE] 1. Go to Firebase Console > Project Settings > Cloud Messaging');
      console.error('[PUSH-SERVICE] 2. Generate/get your Web Push certificates (VAPID key)');
      console.error('[PUSH-SERVICE] 3. Add VITE_APP_VAPID_KEY=your_vapid_key to your .env file');
      console.error('[PUSH-SERVICE] 4. Restart the development server');
      return false;
    }

    if (!('serviceWorker' in navigator)) {
      console.warn('[PUSH-SERVICE] ⚠️ Service workers not supported in this browser');
      return false;
    }

    if (!('Notification' in window)) {
      console.warn('[PUSH-SERVICE] ⚠️ Notifications not supported in this browser');
      return false;
    }

    console.log('[PUSH-SERVICE] ✅ Web environment validation passed');
    return true;
  }

  /**
   * Ensure permissions are granted, requesting them if needed
   */
  private static async ensurePermissions(): Promise<boolean> {
    let permission = await this.checkPermissions();
    if (permission !== 'granted') {
      permission = await this.requestPermissions();
    }
    return permission === 'granted';
  }

  /**
   * Setup native platform notification listeners
   */
  private static setupNativeListeners(): void {
    if (this.listenersSetup) return;

    console.warn('🔔 [PUSH-SERVICE] Setting up native notification listeners...');

    // Firebase Messaging listeners (only if plugin is available)
    if (Capacitor.isPluginAvailable('FirebaseMessaging')) {
      FirebaseMessaging.addListener('notificationReceived', (notification) => {
        console.warn('🔔 [PUSH-SERVICE] Notification received in foreground');
        this.handleNotification('user_notification_receive', notification);
      });
    } else {
      console.warn('🔔 [PUSH-SERVICE] FirebaseMessaging plugin not available, skipping Firebase listeners');
    }

    /* REMOVED to prevent double handling.
     * The `PushNotifications.addListener('pushNotificationActionPerformed', ...)` is more reliable
     * for handling taps, especially when the app is opened from a terminated state.
     * Using both can lead to race conditions and inconsistent behavior.
    FirebaseMessaging.addListener('notificationActionPerformed', (notification) => {
      console.warn('🔔 [PUSH-SERVICE] Notification action performed (user tapped) - Firebase');
      this.handleNotification('user_notification_open', notification);
    });
    */

    // Capacitor PushNotifications listeners (especially important for iOS)
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.warn('🔔 [PUSH-SERVICE] Push notification received - Capacitor');
      console.warn('🔔 [PUSH-SERVICE] Capacitor notification structure:');
      console.warn('🔔 [PUSH-SERVICE] notification.id:', notification.id);
      console.warn('🔔 [PUSH-SERVICE] notification.title:', notification.title);
      console.warn('🔔 [PUSH-SERVICE] notification.body:', notification.body);
      console.warn('🔔 [PUSH-SERVICE] notification.data:', JSON.stringify(notification.data, null, 2));
      this.handleNotification('user_notification_receive', notification);
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      console.warn('🔔 [PUSH-SERVICE] 🚨 Push notification action performed (user tapped) - Capacitor 🚨');
      console.warn('🔔 [PUSH-SERVICE] Capacitor action notification structure:');
      console.warn('🔔 [PUSH-SERVICE] notification.actionId:', notification.actionId);
      console.warn('🔔 [PUSH-SERVICE] notification.inputValue:', notification.inputValue);
      console.warn('🔔 [PUSH-SERVICE] notification.notification:', JSON.stringify(notification.notification, null, 2));
      this.handleNotification('user_notification_open', notification);
    });

    this.listenersSetup = true;
    console.warn('🔔 [PUSH-SERVICE] Native listeners registered successfully');
  }

  /**
   * Setup web platform service worker listeners
   */
  private static setupWebListeners(): void {
    if ('serviceWorker' in navigator) {
      // Setup listener for service worker messages
      if (!this.listenersSetup) {
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.data && event.data.type === MESSAGE_TYPES.NOTIFICATION_CLICK) {
            console.log('[PUSH-SERVICE] 🧭 Received navigation request from service worker:', event.data.route);
            try {
              handleNotificationRoute(event.data.route);
            } catch (error) {
              console.error('[PUSH-SERVICE] Error handling service worker navigation:', error);
            }
          }
        });
        this.listenersSetup = true;
        console.warn('[PUSH-SERVICE] ✅ Web listeners setup successfully');
      }
    }
  }

  /**
   * Check current permission status without requesting them
   */
  static async checkPermissions(): Promise<string> {
    try {
      if (Capacitor.isNativePlatform()) {
        if (!Capacitor.isPluginAvailable('FirebaseMessaging')) {
          console.warn('[PUSH-SERVICE] ❌ FirebaseMessaging plugin not available');
          return 'denied';
        }
        const permissionStatus = await FirebaseMessaging.checkPermissions();
        return permissionStatus.receive;
      } else {
        return Notification.permission;
      }
    } catch (error) {
      console.error('[PUSH-SERVICE] Error checking permissions:', error);
      return 'denied';
    }
  }

  /**
   * Request permissions explicitly
   */
  static async requestPermissions(): Promise<string> {
    try {
      if (Capacitor.isNativePlatform()) {
        if (!Capacitor.isPluginAvailable('FirebaseMessaging')) {
          console.warn('[PUSH-SERVICE] ❌ FirebaseMessaging plugin not available');
          return 'denied';
        }
        const permissionStatus = await FirebaseMessaging.requestPermissions();
        return permissionStatus.receive;
      } else {
        return await Notification.requestPermission();
      }
    } catch (error) {
      console.error('[PUSH-SERVICE] Error requesting permissions:', error);
      return 'denied';
    }
  }

  static async initialize(config: NotificationConfig): Promise<boolean> {
    try {
      console.groupCollapsed('🟢 INITIALIZING PUSH NOTIFICATION SERVICE');
      const platform = Capacitor.getPlatform();
      console.log('⚙️ Platform:', platform);
      if (Capacitor.isNativePlatform()) {
        return await this.initializeNative(config);
      } else if (platform === 'web') {
        return await this.initializeWeb(config);
      } else {
        console.warn('[PUSH-SERVICE] Platform not supported:', platform);
        return false;
      }
    } catch (error) {
      console.error('[PUSH-SERVICE] General error during initialization:', error);
      console.groupEnd();

      // Even if initialization fails, try to set up basic listeners
      console.warn('[PUSH-SERVICE] 🔄 Attempting to setup basic listeners after failed initialization...');
      try {
        await this.setupBasicListeners();
      } catch (listenerError) {
        console.error('[PUSH-SERVICE] Failed to setup basic listeners:', listenerError);
      }

      return false;
    }
  }

  private static async initializeNative(config: NotificationConfig): Promise<boolean> {
    try {
      console.groupCollapsed('📱INITIALIZING NATIVE PUSH SERVICE');
      console.log('[PUSH-SERVICE] Initializing for native platform');

      // Check if FirebaseMessaging plugin is available
      if (!Capacitor.isPluginAvailable('FirebaseMessaging')) {
        console.warn('[PUSH-SERVICE] ❌ FirebaseMessaging plugin not available on this device');
        console.warn('[PUSH-SERVICE] 🔄 Setting up basic listeners as fallback...');
        await this.setupBasicListeners();
        console.groupEnd();
        return false;
      }

      // Use the helper method for permission handling
      const hasPermissions = await this.ensurePermissions();
      if (!hasPermissions) {
        console.warn('❌📱 Permissions denied for native platform');
        return false;
      }

      console.log('[PUSH-SERVICE] 🎯 Permission granted, getting FCM token...');
      const token = await this.getToken();
      if (!token) {
        console.warn('[PUSH-SERVICE] ❌ Failed to get FCM token');
        console.groupEnd();
        return false;
      }
      console.log('🔔 FCM Token received:', token.slice(0, 10) + '...');
      await this.sendTokenToBackend(config);

      // Setup listeners using the helper method
      this.setupNativeListeners();

      // Setup app state listener for badge clearing
      await setupAppStateListener();

      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('[PUSH-SERVICE] Error in native initialization:', error);
      console.groupEnd();
      return false;
    }
  }

  private static async initializeWeb(config: NotificationConfig): Promise<boolean> {
    try {
      console.groupCollapsed('💻 INITIALIZING WEB PUSH SERVICE');
      console.log('[PUSH-SERVICE] Initializing for web platform');

      // Validate environment before proceeding
      if (!this.validateWebEnvironment()) {
        console.warn('❌💻 Web environment validation failed');
        return false;
      }

      // Register service worker with error handling
      let registration: ServiceWorkerRegistration;
      try {
        registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        console.log('[PUSH-SERVICE] Service worker registered');

        // Wait for the service worker to be ready/active
        const installingWorker = registration.installing;
        if (installingWorker) {
          console.log('[PUSH-SERVICE] Service worker installing, waiting...');
          await new Promise<void>((resolve, reject) => {
            // Timeout to prevent indefinite hang when SW gets stuck in 'installed' state
            // (e.g. multi-tab scenario where skipWaiting() isn't called). Fixes BLOOM-FRONTEND-WEB-10R.
            const timeout = setTimeout(() => {
              reject(new Error('Service worker installation timed out (stuck in installed state)'));
            }, 10000);
            installingWorker.addEventListener('statechange', () => {
              // Capture installingWorker reference (not registration.installing) because
              // registration.installing becomes null once the SW transitions to 'installed',
              // causing a TypeError when the statechange event fires.
              if (installingWorker.state === 'activated') {
                clearTimeout(timeout);
                resolve();
              } else if (installingWorker.state === 'redundant') {
                clearTimeout(timeout);
                reject(new Error('Service worker installation failed (state: redundant)'));
              }
            });
          });
        } else if (!registration.active) {
          console.log('[PUSH-SERVICE] Waiting for service worker to be active...');
          await navigator.serviceWorker.ready;
        }

        console.log('[PUSH-SERVICE] Service worker is active and ready');
      } catch (swError) {
        console.error('[PUSH-SERVICE] Service worker registration failed:', swError);
        console.warn('[PUSH-SERVICE] Falling back to basic listeners...');

        // Try to setup basic listeners even if service worker registration fails
        try {
          await this.setupBasicListeners();
        } catch (listenerError) {
          console.error('[PUSH-SERVICE] Failed to setup fallback listeners:', listenerError);
        }
        return false;
      }

      // Use the helper method for permission handling
      const hasPermissions = await this.ensurePermissions();
      if (!hasPermissions) {
        console.warn('❌💻 Web permissions denied');
        return false;
      }

      console.log('[PUSH-SERVICE] 🎯 Web permissions granted');
      const { getMessagingInstance } = await import('../config/firebase.config');
      const { getToken, onMessage } = await import('firebase/messaging');

      const messaging = await getMessagingInstance();
      if (!messaging) {
        console.warn('[PUSH-SERVICE] ❌ Firebase messaging not supported in this browser');
        console.groupEnd();
        return false;
      }

      const currentToken = await getToken(messaging, {
        vapidKey: getVapidKey(),
        serviceWorkerRegistration: registration,
      });

      if (currentToken) {
        this.currentToken = currentToken;
        console.log('🔔 Web token received:', currentToken.slice(0, 10) + '...');
        await this.sendTokenToBackend(config);
      }

      onMessage(messaging, (payload) => {
        console.log('[PUSH-SERVICE] 📨 Message received:', payload);
      });

      // Setup web listeners using the helper method
      this.setupWebListeners();

      // Setup app state listener for badge clearing
      await setupAppStateListener();

      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('[PUSH-SERVICE] Error in web initialization:', error);
      console.groupEnd();
      return false;
    }
  }

  private static async handleNotification(eventName: string, notification: any): Promise<void> {
    console.warn('🔔 [PUSH-SERVICE] ===== HANDLING NOTIFICATION EVENT =====');
    try {
      console.warn(`[PUSH-SERVICE] Event: ${eventName}`);
      console.warn(`[PUSH-SERVICE] Platform: ${Capacitor.getPlatform()}`);
      console.log('[PUSH-SERVICE] Full notification object:', JSON.stringify(notification, null, 2));

      const appState = isAppInForeground ? 'foreground' : 'background';

      // Extract notification data - the backend sends data directly in the 'data' field
      let notificationData: NotificationData = {};

      if (Capacitor.isNativePlatform()) {
        console.warn('[PUSH-SERVICE] Processing native notification...');

        // For native platforms, try multiple possible locations for data
        console.log('[PUSH-SERVICE] Checking notification.data:', notification.data);
        console.log('[PUSH-SERVICE] Checking notification.notification:', notification.notification);

        if (notification.data) {
          notificationData = notification.data;
          console.warn('[PUSH-SERVICE] Found data in notification.data:', JSON.stringify(notificationData));
        } else if (notification.notification?.data) {
          notificationData = notification.notification.data;
          console.warn(
            '[PUSH-SERVICE] Found data in notification.notification.data:',
            JSON.stringify(notificationData),
          );
        } else if (notification.notification?.additionalData) {
          notificationData = notification.notification.additionalData;
          console.warn(
            '[PUSH-SERVICE] Found data in notification.notification.additionalData:',
            JSON.stringify(notificationData),
          );
        } else if (notification.actionId && notification.data) {
          // Capacitor PushNotifications format (especially for iOS)
          notificationData = notification.data;
          console.warn(
            '[PUSH-SERVICE] Found data in Capacitor PushNotifications format:',
            JSON.stringify(notificationData),
          );
        } else {
          // Try to extract from the notification object itself
          console.warn('[PUSH-SERVICE] No data found in expected locations, checking notification object itself');
          const possibleKeys = ['symbol', 'route', 'type', 'sales_amount', 'time_period'];
          for (const key of possibleKeys) {
            if (notification[key]) {
              notificationData[key] = notification[key];
              console.warn(`[PUSH-SERVICE] Found ${key} in notification root:`, notification[key]);
            }
            if (notification.notification?.[key]) {
              notificationData[key] = notification.notification[key];
              console.warn(`[PUSH-SERVICE] Found ${key} in notification.notification:`, notification.notification[key]);
            }
          }
        }

        console.warn('[PUSH-SERVICE] Final native notification data:', JSON.stringify(notificationData));
      } else {
        // For web platform, check different possible locations
        console.log('[PUSH-SERVICE] Processing web notification...');
        notificationData = notification.data || notification.notification?.data || {};
        console.log('[PUSH-SERVICE] Web notification data:', notificationData);
      }

      // Log analytics event (wrapped in try-catch so analytics failures don't block navigation)
      const eventData = {
        name: eventName,
        params: {
          notification_type: notificationData.type || 'unknown',
          symbols: notificationData.symbol || '',
          platform: Capacitor.getPlatform(),
          app_state: appState,
          timestamp: new Date().toISOString(),
          has_route: !!notificationData.route,
          route: notificationData.route || '',
        },
      };
      console.log(`[PUSH-SERVICE] Event: ${eventName}`, JSON.stringify(eventData, null, 2));
      try {
        await FirebaseAnalytics.logEvent(eventData);
      } catch (analyticsError) {
        console.warn('[PUSH-SERVICE] Analytics event failed (non-blocking):', analyticsError);
      }

      // Handle navigation when notification is opened (tapped)
      if (eventName === 'user_notification_open') {
        console.warn('[PUSH-SERVICE] 🚨 Notification was opened/tapped');
        console.warn('[PUSH-SERVICE] 🔍 Available notification data:', JSON.stringify(notificationData, null, 2));

        // Handle navigation - only if route is explicitly provided
        if (notificationData.route) {
          console.warn('[PUSH-SERVICE] 🧭 NAVIGATING TO ROUTE:', notificationData.route);

          // Handle navigation FIRST
          try {
            handleNotificationRoute(notificationData.route);
            console.warn('[PUSH-SERVICE] ✅ Navigation completed successfully');
          } catch (error) {
            console.error('[PUSH-SERVICE] ❌ Error handling notification route:', error);
            console.error('[PUSH-SERVICE] ❌ Error details:', error);
          }
        } else {
          console.warn('[PUSH-SERVICE] ⚠️ No route found in notification data, navigating to default');
          // Navigate to default portfolios page when no route is provided
          handleNotificationRoute('/portfolios');
        }

        // Mark notification as read AFTER navigation (so navigation isn't blocked)
        if (notificationData.notification_id && this.currentToken) {
          console.warn('[PUSH-SERVICE] 📖 Marking notification as read:', notificationData.notification_id);
          try {
            await markNotificationAsRead(notificationData.notification_id, this.currentToken);
            console.warn('[PUSH-SERVICE] ✅ Notification marked as read successfully');
          } catch (error) {
            console.error('[PUSH-SERVICE] ❌ Error marking notification as read:', error);
          }
        } else {
          console.warn('[PUSH-SERVICE] ⚠️ No notification_id or token available for marking as read');
        }
      }
    } catch (error) {
      console.error(`[PUSH-SERVICE] Error handling notification event "${eventName}":`, error);
    }
  }

  static async updateConfig(config: NotificationConfig): Promise<boolean> {
    // Don't auto-initialize if not already initialized
    // This prevents automatic permission requests
    if (!this.isInitialized) {
      console.warn('[PUSH-SERVICE] Service not initialized, skipping config update');
      console.warn('[PUSH-SERVICE] Call PushNotificationService.initialize() first to enable notifications');
      return false;
    }

    if (!this.currentToken) {
      console.warn('[PUSH-SERVICE] No token available, cannot update config');
      return false;
    }

    try {
      console.log('[PUSH-SERVICE] Updating config with symbols:', config.watchlistSymbols);
      await this.sendTokenToBackend(config);
      return true;
    } catch (error) {
      console.error('[PUSH-SERVICE] Error updating config:', error);
      return false;
    }
  }

  private static async sendTokenToBackend(config: NotificationConfig): Promise<void> {
    console.groupCollapsed('📡 SENDING TOKEN TO BACKEND');
    if (!this.currentToken) {
      throw new Error('[PUSH-SERVICE] No token available');
    }

    const maxRetries = 3;
    let attempts = 0;
    let success = false;

    while (!success && attempts < maxRetries) {
      attempts++;
      try {
        console.log(`[PUSH-SERVICE] Registering device (attempt ${attempts}/${maxRetries}):`, {
          token: this.currentToken.slice(0, 10) + '...',
          notificationTypes: config.notificationTypes,
          watchlistSymbols: config.watchlistSymbols,
        });

        const rcUserId = await getRevenueCatAppUserId();
        // TODO: Extend when adding languages beyond es/en (see also MorePage and notification_translator.py)
        const currentLang = i18n.language?.startsWith('es') ? 'es' : 'en';
        await registerDevice(this.currentToken, Capacitor.getPlatform(), {
          notificationTypes: config.notificationTypes,
          watchlistSymbols: config.watchlistSymbols,
          revenuecatUserId: rcUserId,
          language: currentLang,
        });

        success = true;
        console.log('[PUSH-SERVICE] Device registered successfully');
      } catch (error) {
        if (attempts < maxRetries) {
          console.warn(`[PUSH-SERVICE] Registration attempt ${attempts} failed, retrying...`, error);
          // Wait before retrying (exponential backoff: 1s, 2s, 4s)
          await new Promise((resolve) => setTimeout(resolve, 1000 * Math.pow(2, attempts - 1)));
        } else {
          console.error('[PUSH-SERVICE] All registration attempts failed:', error);
          throw error;
        }
      }
    }

    console.groupEnd();
  }

  /**
   * Test navigation functionality manually
   * Call this from the browser console to test: window.PushNotificationService.testNavigation()
   */
  static testNavigation(route = '/symbol/AAPL'): void {
    console.warn('[PUSH-SERVICE] 🧪 Testing navigation to:', route);
    console.warn('[PUSH-SERVICE] 🧪 Simulating notification tap event...');

    try {
      // Simulate a notification tap event
      this.handleNotification('user_notification_open', {
        data: {
          route: route,
          symbol: 'AAPL',
          type: 'test',
        },
      });
      console.warn('[PUSH-SERVICE] ✅ Navigation test completed');
    } catch (error) {
      console.error('[PUSH-SERVICE] ❌ Navigation test failed:', error);
    }
  }

  /**
   * Setup basic notification listeners without full initialization
   * This ensures we can still handle notifications even if full init fails
   */
  static async setupBasicListeners(): Promise<void> {
    if (this.listenersSetup) return;

    console.warn('[PUSH-SERVICE] 🔧 Setting up basic notification listeners...');

    try {
      if (Capacitor.isNativePlatform()) {
        // Use the existing native listeners setup method
        this.setupNativeListeners();
      } else if (Capacitor.getPlatform() === 'web') {
        // Use the existing web listeners setup method
        this.setupWebListeners();
      }

      console.warn('[PUSH-SERVICE] ✅ Basic listeners setup successfully');
    } catch (error) {
      console.error('[PUSH-SERVICE] Error setting up basic listeners:', error);
      throw error;
    }
  }
}
